/**
 * Phase 8 chunk 2A — progressive-enhancement sort hijack for /text/*
 * table views.
 *
 * Server-side `?sort=col&dir=asc|desc` works without JS — every table
 * header is an `<a href>` link the user can click to navigate. This
 * script intercepts those clicks when JS is available + re-sorts the
 * existing `<tbody>` rows in place, swaps the arrow indicator, and
 * updates the URL via `history.replaceState` so the link stays
 * shareable.
 *
 * Loaded as a separate Vite entry (no Cesium, no Lit) and pulled into
 * `/text/layout.php` via `<script type="module" defer>` so the
 * baseline `/text` page weight stays tiny.
 *
 * **Drift caveat:** the JS comparator is `localeCompare` with
 * `numeric: true` + `sensitivity: 'base'`, plus a numeric path for
 * `data-sort-type="number"` and ISO-date path for `data-sort-type="date"`.
 * It approximates SQLite's `COLLATE NOCASE` ordering but is not bit-
 * identical (collation tie-breaking can differ). Tradeoff: the
 * hijack feels instant. If perfect parity matters for a specific
 * column, omit the `data-sort-col` attribute on its `<a>` so the
 * link falls through to a server round-trip.
 */

type ColumnType = 'string' | 'number' | 'date';

function compareValues(a: string, b: string, type: ColumnType): number {
  switch (type) {
    case 'number': {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
      if (Number.isNaN(na)) return 1;
      if (Number.isNaN(nb)) return -1;
      return na - nb;
    }
    case 'date': {
      const ta = Date.parse(a);
      const tb = Date.parse(b);
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    }
    case 'string':
    default:
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }
}

/**
 * Locate the column index of a header `<a>` within its table's
 * thead row. Returns -1 if the structure isn't recognized.
 */
function findColumnIndex(link: HTMLAnchorElement, table: HTMLTableElement): number {
  const headerRow = table.tHead?.rows[0];
  if (!headerRow) return -1;
  for (let i = 0; i < headerRow.cells.length; i++) {
    if (headerRow.cells[i].contains(link)) return i;
  }
  return -1;
}

/**
 * Sort the rows of `<tbody>` by the values at column index `col`,
 * using a comparator picked by `type`. Re-appends rows in the new
 * order. Stable as of every modern JS engine.
 */
function sortRows(table: HTMLTableElement, col: number, dir: 'asc' | 'desc', type: ColumnType): void {
  const tbody = table.tBodies[0];
  if (!tbody) return;
  const rows = Array.from(tbody.rows);
  rows.sort((rowA, rowB) => {
    const a = rowA.cells[col]?.textContent?.trim() ?? '';
    const b = rowB.cells[col]?.textContent?.trim() ?? '';
    const cmp = compareValues(a, b, type);
    return dir === 'asc' ? cmp : -cmp;
  });
  tbody.append(...rows);
}

/**
 * Swap arrow indicators on every column header: clear all existing
 * arrows, then add `↑` or `↓` to the currently-active column.
 */
function updateArrows(table: HTMLTableElement, activeCol: string, dir: 'asc' | 'desc'): void {
  const headers = table.querySelectorAll<HTMLAnchorElement>('thead a[data-sort-col]');
  for (const a of headers) {
    const existing = a.querySelector('span[aria-hidden="true"]');
    if (existing) existing.remove();
    if (a.dataset.sortCol === activeCol) {
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = ' ' + (dir === 'asc' ? '↑' : '↓');
      a.appendChild(span);
    }
  }
}

/** The click-event handler factored out so Vitest can test the module
 *  without `document` existing in scope. The browser entry point calls
 *  `attachSortHijack()` below at module load time. */
export function handleSortClick(event: MouseEvent): void {
  const target = event.target as Element | null;
  if (target === null) return;

  // Walk up to the sort-aware anchor, if any.
  const link = target.closest<HTMLAnchorElement>('a[data-sort-col]');
  if (link === null) return;

  // Modifier-clicks (open in new tab / window) should fall through
  // to the browser's default behavior. Plain left-clicks get hijacked.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.button !== 0) return;

  const table = link.closest<HTMLTableElement>('table');
  if (table === null) return;

  const url = new URL(link.href, window.location.href);
  const col = link.dataset.sortCol ?? '';
  const dirParam = url.searchParams.get('dir');
  if (dirParam !== 'asc' && dirParam !== 'desc') return;
  const type: ColumnType =
    link.dataset.sortType === 'number' ? 'number'
      : link.dataset.sortType === 'date' ? 'date'
        : 'string';

  const colIdx = findColumnIndex(link, table);
  if (colIdx < 0) return;

  // All gates passed — intercept + sort in place.
  event.preventDefault();
  sortRows(table, colIdx, dirParam, type);
  updateArrows(table, col, dirParam);

  // Update the URL so the resulting view is copy-shareable. Use
  // replaceState (not pushState) so the back button doesn't fill
  // with one entry per click.
  history.replaceState(history.state, '', url.pathname + url.search);
}

/** Browser entry — wires the click listener. No-op when `document`
 *  doesn't exist (Vitest's node env, SSR, etc.). */
export function attachSortHijack(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('click', handleSortClick);
}

attachSortHijack();

export { compareValues, sortRows, updateArrows };
export type { ColumnType };
